import { LogOut, ShieldAlert } from "lucide-react";

import { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import { Label } from "@/components/ui/label";

import { GridPattern } from "@/components/ui/grid-pattern";

import { setupTwoFactor, verifyTwoFactorSetup } from "../api/profileApi";

import { useAuth } from "../hooks/useAuth";

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object") {
    const maybeAxios = error as {
      response?: { data?: { message?: string | string[] } };
    };

    const message = maybeAxios.response?.data?.message;

    if (Array.isArray(message)) return message.join(", ");

    if (typeof message === "string") return message;
  }

  return fallback;
};

/**
 * Blocking 2FA enrolment screen. Reached via ProtectedRoute when an
 * authenticated admin still has twoFactorEnabled === false. The only escape
 * hatches are completing enrolment or signing out — there is no "later" button
 * on purpose (that's what /recommend-2fa is for, kept around for soft prompts).
 */

const MandatoryTwoFactorPage = () => {
  const navigate = useNavigate();

  const { session, signOut, updateSessionUser } = useAuth();

  const [setupData, setSetupData] = useState<{
    qrCodeUrl: string;

    secret: string;
  } | null>(null);

  const [code, setCode] = useState("");

  const [loading, setLoading] = useState(true);

  const [verifying, setVerifying] = useState(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session.user?.twoFactorEnabled) {
      navigate("/", { replace: true });
    }
  }, [session.user?.twoFactorEnabled, navigate]);

  const startSetup = useCallback(async () => {
    try {
      setLoading(true);

      setError(null);

      const data = await setupTwoFactor();

      setSetupData(data);
    } catch (e) {
      setError(
        getErrorMessage(e, "No pudimos iniciar la configuración de 2FA."),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startSetup();
  }, [startSetup]);

  const handleVerify = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (code.length !== 6) return;

    try {
      setVerifying(true);

      setError(null);

      await verifyTwoFactorSetup(code);

      updateSessionUser({ twoFactorEnabled: true });

      navigate("/", { replace: true });
    } catch (e) {
      setError(getErrorMessage(e, "Código inválido. Intenta nuevamente."));

      setCode("");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden bg-background">
      <GridPattern
        width={40}
        height={40}
        className="[mask-image:radial-gradient(600px_circle_at_center,white,transparent)] [stroke:var(--primary)] [stroke-opacity:0.2]"
      />

      <Card className="relative z-10 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 mb-4">
            <ShieldAlert className="h-7 w-7 text-red-500" />
          </div>
          <CardTitle className="text-2xl">
            Autenticación de dos factores obligatoria
          </CardTitle>
          <CardDescription>
            Para proteger el panel de administración, debés activar 2FA antes de
            continuar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-500">
              {error}
            </div>
          )}

          {loading && !setupData && (
            <p className="text-sm text-muted-foreground text-center">
              Generando código QR…
            </p>
          )}

          {setupData && (
            <>
              <div className="flex flex-col items-center gap-3">
                <img
                  src={setupData.qrCodeUrl}
                  alt="QR code para configurar 2FA"
                  className="h-48 w-48 rounded-md border bg-white p-2"
                />
                <p className="text-xs text-muted-foreground text-center">
                  Escaneá con Google Authenticator, 1Password, Authy o similar.
                  Si no podés escanear, ingresá la clave manualmente:
                </p>
                <code className="select-all rounded bg-muted px-2 py-1 text-xs font-mono">
                  {setupData.secret}
                </code>
              </div>

              <form onSubmit={handleVerify} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="totp-code">Código de 6 dígitos</Label>
                  <Input
                    id="totp-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) =>
                      setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="123456"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={code.length !== 6 || verifying}
                >
                  {verifying ? "Verificando…" : "Activar 2FA"}
                </Button>
              </form>
            </>
          )}

          <Button
            type="button"
            variant="ghost"
            className="w-full text-muted-foreground"
            onClick={() => {
              signOut();

              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Cerrar sesión
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default MandatoryTwoFactorPage;
